import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useTeamRoster } from "../../../hooks/useTeamRoster";
import { backendService } from "../../../services/backend.service";
import { isNurseActiveInDepartment } from "../../../utils/formatters";
import { EmploymentType, type NurseFullRole, type TeamNurse } from "../../../utils/models";
import { TEAM_SECTIONS } from "../config";
import type { BulkNurseWelcomeResponse, LoginStatusFilter, ResendWelcomeNursePreflight } from "./types";
import { RESEND_WELCOME_PREFLIGHT_QUERY_KEY, useWelcomeSendHistory } from "./useWelcomeSendHistory";

/** Contract-type sections ticked when the dialog opens. Agency is deliberately absent — decision 5 of the plan. */
const DEFAULT_SECTION_TITLES: readonly string[] = TEAM_SECTIONS.filter((section) => section.title !== "Agency" && section.title !== "Unknown").map(
	(section) => section.title,
);

/** The text key the backend sends when it can name the problem. Null when it cannot. */
function uiVisibleErrorKey(error: unknown): string | null {
	const detail = (error as { response?: { data?: { detail?: { message?: unknown } } } })?.response?.data?.detail;
	return typeof detail?.message === "string" && detail.message.length > 0 ? detail.message : null;
}

/** The section a nurse's employment type belongs to, so "Agency" covers Travel + PerDiem as it does everywhere else. */
function sectionTitleOf(employmentType: EmploymentType): string {
	return TEAM_SECTIONS.find((section) => section.employmentTypes.includes(employmentType))?.title ?? EmploymentType.Unknown;
}

export type ResendWelcomeState = ReturnType<typeof useResendWelcomeState>;

/**
 * All of the dialog's filtering, selection and send logic.
 *
 * Two populations, and the difference between them is the whole point of the copy: `pool` is everyone
 * matching Role AND Employment type, and is what the staff picker lists — so someone who has already
 * logged in can still be added by hand. `eligible` narrows the pool by the login-status chip, and is
 * what the selection snaps back to whenever a filter changes.
 */
export const useResendWelcomeState = ({ open }: { open: boolean }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const departmentId = chosenDepartment.department_id;
	const { roster } = useTeamRoster(departmentId);

	const [selectedRoles, setSelectedRoles] = useState<NurseFullRole[]>([]);
	const [selectedSections, setSelectedSections] = useState<string[]>([...DEFAULT_SECTION_TITLES]);
	const [loginFilter, setLoginFilter] = useState<LoginStatusFilter>("never");
	const [loginChipShown, setLoginChipShown] = useState(true);
	const [selectedNurseIds, setSelectedNurseIds] = useState<ReadonlySet<number>>(new Set());
	const [message, setMessage] = useState("");
	const [result, setResult] = useState<BulkNurseWelcomeResponse | null>(null);

	const { query: preflight } = useWelcomeSendHistory({ enabled: open });
	const queryClient = useQueryClient();

	/** Send-readiness per nurse. Absent from the map = the backend does not consider them sendable at all. */
	const preflightByNurseId = useMemo(() => {
		const map = new Map<number, ResendWelcomeNursePreflight>();
		for (const entry of preflight.data?.nurses ?? []) map.set(entry.nurseId, entry);
		return map;
	}, [preflight.data]);

	/** Only staff the backend listed as sendable, and only those still active here. */
	const candidates = useMemo<TeamNurse[]>(
		() => roster.filter((nurse) => preflightByNurseId.has(nurse.id) && isNurseActiveInDepartment(nurse, departmentId)),
		[roster, preflightByNurseId, departmentId],
	);

	/** Roles present in the candidate list — the Role filter offers what this department actually has. */
	const availableRoles = useMemo<NurseFullRole[]>(() => {
		const seen = new Set<NurseFullRole>();
		for (const nurse of candidates) seen.add(nurse.role);
		return Array.from(seen).sort((a, b) => a.localeCompare(b));
	}, [candidates]);

	const roleCounts = useMemo(() => {
		const counts = new Map<NurseFullRole, number>();
		for (const nurse of candidates) counts.set(nurse.role, (counts.get(nurse.role) ?? 0) + 1);
		return counts;
	}, [candidates]);

	const sectionCounts = useMemo(() => {
		const counts = new Map<string, number>();
		candidates.forEach((nurse) => {
			const title = sectionTitleOf(nurse.employmentType);
			counts.set(title, (counts.get(title) ?? 0) + 1);
		});
		return counts;
	}, [candidates]);

	/** Everyone matching Role AND Employment type. What the picker lists. */
	const pool = useMemo<TeamNurse[]>(
		() =>
			candidates
				.filter((nurse) => selectedRoles.includes(nurse.role) && selectedSections.includes(sectionTitleOf(nurse.employmentType)))
				.sort((a, b) => formatPersonName(a).localeCompare(formatPersonName(b))),
		[candidates, selectedRoles, selectedSections, formatPersonName],
	);

	/** The pool narrowed by the login chip. What the selection resets to. */
	const eligible = useMemo<TeamNurse[]>(() => {
		if (!loginChipShown || loginFilter === "all") return pool;
		return pool.filter((nurse) => !nurse.lastLoginTime);
	}, [pool, loginChipShown, loginFilter]);

	const resetSelection = useCallback((nextEligible: TeamNurse[]) => {
		setSelectedNurseIds(new Set(nextEligible.map((nurse) => nurse.id)));
	}, []);

	// Opening resets the filters, so the dialog never sends to a different set than its title says.
	// Keyed on `open` alone: `availableRoles` changes on any background refresh, which would wipe a
	// half-typed message. The effect below seeds the roles.
	useEffect(() => {
		if (!open) return;
		setSelectedSections([...DEFAULT_SECTION_TITLES]);
		setLoginFilter("never");
		setLoginChipShown(true);
		// We already know the facility, so its name is written in, not left as a variable.
		setMessage(t("team.resend_welcome.compose.default_body", { facilityName: chosenDepartment.display_name, firstNameToken: "{first_name}" }));
		setResult(null);
	}, [open, t, chosenDepartment.display_name]);

	// `availableRoles` can arrive after the dialog opens. Merge, never replace: replacing would undo a
	// narrowing the user has already made.
	const [rolesSeeded, setRolesSeeded] = useState(false);
	useEffect(() => {
		if (!open) {
			setRolesSeeded(false);
			return;
		}
		if (rolesSeeded || availableRoles.length === 0) return;
		setSelectedRoles(availableRoles);
		setRolesSeeded(true);
	}, [open, availableRoles, rolesSeeded]);

	// Selection follows the eligible set until the user touches the picker. Every filter handler below
	// calls `resetSelection` explicitly (the design's contract), and this covers the first paint.
	const [selectionSeeded, setSelectionSeeded] = useState(false);
	useEffect(() => {
		if (!open) {
			setSelectionSeeded(false);
			return;
		}
		if (selectionSeeded || eligible.length === 0) return;
		resetSelection(eligible);
		setSelectionSeeded(true);
	}, [open, eligible, selectionSeeded, resetSelection]);

	const handleRolesChange = useCallback(
		(roles: NurseFullRole[]) => {
			setSelectedRoles(roles);
			const nextPool = candidates.filter((nurse) => roles.includes(nurse.role) && selectedSections.includes(sectionTitleOf(nurse.employmentType)));
			resetSelection(loginChipShown && loginFilter === "never" ? nextPool.filter((nurse) => !nurse.lastLoginTime) : nextPool);
		},
		[candidates, selectedSections, loginChipShown, loginFilter, resetSelection],
	);

	const handleSectionsChange = useCallback(
		(sections: string[]) => {
			setSelectedSections(sections);
			const nextPool = candidates.filter((nurse) => selectedRoles.includes(nurse.role) && sections.includes(sectionTitleOf(nurse.employmentType)));
			resetSelection(loginChipShown && loginFilter === "never" ? nextPool.filter((nurse) => !nurse.lastLoginTime) : nextPool);
		},
		[candidates, selectedRoles, loginChipShown, loginFilter, resetSelection],
	);

	const handleLoginFilterChange = useCallback(
		(next: LoginStatusFilter) => {
			setLoginFilter(next);
			resetSelection(next === "never" ? pool.filter((nurse) => !nurse.lastLoginTime) : pool);
		},
		[pool, resetSelection],
	);

	/** Removing the chip means "no login restriction" — the same state as picking "All staff". */
	const handleRemoveLoginFilter = useCallback(() => {
		setLoginChipShown(false);
		setLoginFilter("all");
		resetSelection(pool);
	}, [pool, resetSelection]);

	const handleAddLoginFilter = useCallback(() => {
		setLoginChipShown(true);
		setLoginFilter("never");
		resetSelection(pool.filter((nurse) => !nurse.lastLoginTime));
	}, [pool, resetSelection]);

	const toggleNurse = useCallback((nurseId: number) => {
		setSelectedNurseIds((previous) => {
			const next = new Set(previous);
			if (next.has(nurseId)) next.delete(nurseId);
			else next.add(nurseId);
			return next;
		});
	}, []);

	const selectedNurses = useMemo(() => pool.filter((nurse) => selectedNurseIds.has(nurse.id)), [pool, selectedNurseIds]);

	/** A nurse whose most recent welcome went out today, in the department's own day. */
	const wasInvitedToday = useCallback((nurseId: number): boolean => preflightByNurseId.get(nurseId)?.sentToday === true, [preflightByNurseId]);

	const alreadyInvitedTodayCount = useMemo(() => selectedNurses.filter((nurse) => wasInvitedToday(nurse.id)).length, [selectedNurses, wasInvitedToday]);

	// No mobile but an email on file: they still get the message, just by email.
	const noMobileCount = useMemo(
		() => selectedNurses.filter((nurse) => preflightByNurseId.get(nurse.id)?.hasMobile === false && preflightByNurseId.get(nurse.id)?.hasEmail === true).length,
		[selectedNurses, preflightByNurseId],
	);

	// Neither a phone number nor an email, so there is no way to reach them at all.
	const noContactCount = useMemo(
		() =>
			selectedNurses.filter((nurse) => preflightByNurseId.get(nurse.id)?.hasMobile === false && preflightByNurseId.get(nurse.id)?.hasEmail === false).length,
		[selectedNurses, preflightByNurseId],
	);

	const sendMutation = useMutation({
		mutationFn: async () => {
			// Send exactly who the dialog counted and warned about. The raw id set can still hold
			// people the filters dropped, who were never checked for "already invited today".
			const nurseIds = selectedNurses.map((nurse) => nurse.id);
			const customMessage = message.trim() ? message : null;
			track("team_resend_welcome_send_start", {
				department_id: departmentId,
				recipient_count: nurseIds.length,
				used_custom_message: customMessage !== null,
			});
			const { data } = await backendService.sendBulkNurseWelcome({ departmentId, nurseIds, customMessage });
			return data;
		},
		onSuccess: (data) => {
			setResult(data);
			// The Last Login column reads from this query, so without it the grid still says "Never".
			queryClient.invalidateQueries({ queryKey: [RESEND_WELCOME_PREFLIGHT_QUERY_KEY, departmentId] });
			track("team_resend_welcome_send_success", {
				department_id: departmentId,
				recipient_count: data.results.length,
				used_custom_message: message.trim().length > 0,
				sent_by_sms: data.sentBySms,
				sent_by_email: data.sentByEmail,
				failed: data.failed,
			});
			if (data.failed > 0) showSnackbar(t("team.resend_welcome.partial_failure", { count: data.failed }), "warning");
		},
		onError: (error: unknown) => {
			trackError("team_resend_welcome_send_error", {
				department_id: departmentId,
				recipient_count: selectedNurses.length,
				error: error instanceof Error ? error.message : String(error),
			});
			// Some messages may already have gone out. Refresh so the admin can see who got one
			// before trying again, rather than texting them a second time.
			queryClient.invalidateQueries({ queryKey: [RESEND_WELCOME_PREFLIGHT_QUERY_KEY, departmentId] });
			// Show what the backend said when it can explain itself, the generic line when it cannot.
			const messageKey = uiVisibleErrorKey(error);
			showSnackbar(messageKey ? t(messageKey) : t("team.resend_welcome.send_failed_partial"), "error");
		},
	});

	return {
		preflight,
		availableRoles,
		roleCounts,
		sectionCounts,
		selectedRoles,
		handleRolesChange,
		selectedSections,
		handleSectionsChange,
		loginFilter,
		loginChipShown,
		handleLoginFilterChange,
		handleRemoveLoginFilter,
		handleAddLoginFilter,
		pool,
		eligible,
		selectedNurseIds,
		setSelectedNurseIds,
		selectedNurses,
		toggleNurse,
		wasInvitedToday,
		alreadyInvitedTodayCount,
		noMobileCount,
		noContactCount,
		message,
		setMessage,
		sendMutation,
		result,
		invitesSentToday: preflight.data?.invitesSentToday ?? 0,
	};
};
